import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HouseholdService } from 'app/services/household-service';
import { OregonWellDataService } from 'app/services/oregon-well-data.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { FormControl, FormGroup } from '@angular/forms';
import { HouseholdAssociation } from 'app/models/household/household-association';
import { IHousingUnit } from 'app/models/housingunits/housing-unit';
import { IHousingUnitSearch } from 'app/models/housingunits/housing-unit-search';
import { HousingUnitService } from 'app/services/housingunit-service';
import { IWellViewModel } from 'app/models/well/well.viewmodel';
import { Search } from 'app/models/well/search/search';
import { ISearchResult } from 'app/models/well/search/search.result';
import { HousingUnitAssociations } from 'app/models/housingunits/housing-unit-associations';

/*
 * Associate Housing Unit to Well Dialog
 */
@Component({
  selector: 'dialog-associate-housingunit-well',
  templateUrl: 'associate-housingunit-well-dialog.html',
  styleUrls: ['./associate-housingunit-well-dialog.scss']
})
export class DialogAssociateHousingUnitToWellComponent implements OnInit {
  pageSize = 20;
  loading: boolean = false;

  housingUnit: IHousingUnit;
  housingUnits: IHousingUnit[] = [];
  housingUnitSearchResults: IHousingUnit[] = [];
  selectedHousingUnitId: number = 0;
  well: IWellViewModel;
  wells: ISearchResult[] = [];
  wellSearchResults: IWellViewModel[] = [];
  selectedWellId: number = 0;
  flatResults: ISearchResult[] = [];
  searchResults: any[] = [];

  association: HouseholdAssociation = {
    wellId: 0,
    householdId: 0,
    voucherId: 0,
    housingUnitId: 0,
    housingUnitStartDate: '',
    housingUnitEndDate: ''
  }

  housingUnitAssociations: HousingUnitAssociations = {
    households: [],
    housingUnit: undefined,
    householdHousingUnitKey: 0,
    housingUnitStartDate: undefined,
    housingUnitEndDate: undefined,
    housingUnits: [],
    wells: [],
    wellId: 0,
    createNewWell: false,
    newContactName: '',
    newAddress1: '',
    newCity: '',
    newZip: '',
    newCounty: '',
    housingUnitId: 0
  }

  housingUnitSearchForm = new FormGroup({
    contactName: new FormControl(''),
    county: new FormControl(''),
    address1: new FormControl(''),
    city: new FormControl(''),
    zip: new FormControl('')
  });

  housingUnitSearch: IHousingUnitSearch = {
    id: '',
    contactName: '',
    address1: '',
    city: '',
    zip: '',
    searchBy: '',
    searchText: '',
    county: '',
    searchResultsMaxRows: ''
  }

  wellSearchForm = new FormGroup({
    wellId: new FormControl(this.oregonWellDataService.search.wellId),
    sampleId: new FormControl(this.oregonWellDataService.search.sampleId),
    contaminant: new FormControl(this.oregonWellDataService.search.contaminant),
    county: new FormControl(this.oregonWellDataService.search.county),
    address: new FormControl(this.oregonWellDataService.search.address),
    city: new FormControl(this.oregonWellDataService.search.city),
    zip: new FormControl(this.oregonWellDataService.search.zip),
    wellTag: new FormControl(this.oregonWellDataService.search.wellTag),
    wellLog: new FormControl(this.oregonWellDataService.search.wellLog),
    startDate: new FormControl(this.oregonWellDataService.search.startDate),
    endDate: new FormControl(this.oregonWellDataService.search.endDate),
    createStartDate: new FormControl(this.oregonWellDataService.search.createStartDate),
    createEndDate: new FormControl(this.oregonWellDataService.search.createEndDate),
    receivedStartDate: new FormControl(this.oregonWellDataService.search.receivedStartDate),
    receivedEndDate: new FormControl(this.oregonWellDataService.search.receivedEndDate),
    searchBy: new FormControl(this.oregonWellDataService.search.searchBy),
    voucherType: new FormControl(this.oregonWellDataService.search.voucherType)
  });

  wellSearch: Search = {
    wellId: '',
    sampleId: '',
    contaminant: [],
    address: '',
    county: '',
    city: '',
    state: '',
    zip: '',
    wellTag: '',
    wellLog: '',
    startDate: '',
    endDate: '',
    createStartDate: '',
    createEndDate: '',
    receivedStartDate: '',
    receivedEndDate: '',
    isValidated: false,
    searchBy: 'Well',
    searchResultsMaxRows: '',
    voucherType: '',
    voucherOhaId: '',
    voucherIdentifier: ''
  }

  wellForm = new FormGroup({
    createNewWell: new FormControl({ value: false, disabled: false })
  });

  displayedColumnsHousingUnits: string[] = ['action', 'id', 'contactName', 'address1', 'city', 'zip', 'county'];
  dataSourceHousingUnits = new MatTableDataSource<IHousingUnit>(this.housingUnitSearchResults);
  displayedColumnsWells: string[] = ['action', 'id', 'address', 'city', 'zip', 'county'];
  dataSourceWells = new MatTableDataSource<ISearchResult>(this.wells);
  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;

  filterTable(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.dataSourceHousingUnits.filter = value.trim().toLowerCase();
  }

  constructor(public householdService: HouseholdService,
    public oregonWellDataService: OregonWellDataService,
    public housingUnitService: HousingUnitService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: HouseholdAssociation,
    private dialogRef: MatDialogRef<DialogAssociateHousingUnitToWellComponent>) {
    this.loading = true;
    this.association = this.data;
    this.loading = false;
  }

  ngOnInit(): void {
    this.load();
  }

  load() {
    if (this.association.wellId > 0) {
      this.loading = true;
      this.oregonWellDataService.GetEditWell(this.association.wellId).subscribe(result => {
        this.housingUnit = result;
        this.housingUnitSearchForm.controls['contactName'].setValue(this.housingUnit.contactName);
        this.housingUnitSearchForm.controls['address1'].setValue(this.housingUnit.address1);
        this.housingUnitSearchForm.controls['city'].setValue(this.housingUnit.city);
        this.housingUnitSearchForm.controls['zip'].setValue(this.housingUnit.zip);
        this.housingUnitSearchForm.controls['county'].setValue(this.housingUnit.county);
        this.searchHousingUnits();
        this.loading = false;
      },
        error => {
          this.oregonWellDataService.onHttpError(error);
          this.loading = false;
        }
      );
    }

    if (this.association.housingUnitId > 0) {
      this.loading = true;
      this.housingUnitService.getAssociatedHousingUnitById(this.association.housingUnitId).subscribe(result => {
        this.well = result;
        this.wellSearchForm.controls['address'].setValue(this.well.address1);
        this.wellSearchForm.controls['city'].setValue(this.well.city);
        this.wellSearchForm.controls['zip'].setValue(this.well.zip);
        this.wellSearchForm.controls['county'].setValue(this.well.county);
        this.searchWells();
        this.loading = false;
      },
        error => {
          this.oregonWellDataService.onHttpError(error);
          this.loading = false;
        }
      );
    }

    this.loading = false;
  }

  searchHousingUnits() {
    this.setHousingUnitSearchFieldsFromForm();

    if (!this.validHousingUnitSearch()) {
      return;
    }

    this.loading = true;
    this.housingUnitService.search(this.housingUnitSearch).subscribe(
      result => {
        this.housingUnitSearchResults = result;
        this.loading = false;
      },
      error => {
        this.loading = false;
      },
      () => {
        this.dataSourceHousingUnits = new MatTableDataSource<IHousingUnit>(this.housingUnitSearchResults);
        this.dataSourceHousingUnits.paginator = this.paginator;
        this.dataSourceHousingUnits.sort = this.sort;
      }
    );
  }

  getFlatWellSearchResults(): any[] {
    return this.wells.map((result: any) => {
      return {
        wellId: result.data?.wellId || null,
        wellAddress1: result.data?.wellAddress1 || null,
        wellCity: result.data?.wellCity || null,
        wellZip: result.data?.wellZip || null,
        wellCounty: result.data?.wellCounty || null,
        wellTag: result.data?.wellTag || null,
      } as any;
    });
  }

  searchWells() {
    this.setWellSearchFieldsFromForm();

    if (!this.validWellSearch()) {
      return;
    }

    this.loading = true;

    this.oregonWellDataService.Search(this.wellSearch).subscribe(
      result => {
        this.wells = result;
        //this.dataSourceWells = new MatTableDataSource<ISearchResult>(this.wells);
        this.loading = false;
      },
      error => {
        this.loading = false;
      },
      () => {
        this.flatResults = this.getFlatWellSearchResults();
        this.dataSourceWells = new MatTableDataSource<ISearchResult>(this.flatResults);
        this.dataSourceWells.paginator = this.paginator;
        this.dataSourceWells.sort = this.sort;
      }
    );
  }

  validHousingUnitSearch(): boolean {
    if ((this.housingUnitSearch.address1.length == 0) &&
      (this.housingUnitSearch.city.length == 0) &&
      (this.housingUnitSearch.zip.length == 0) &&
      (this.housingUnitSearch.county.length == 0) &&
      (this.housingUnitSearch.contactName.length == 0)) {
      this.toastr.error('Invalid Search.  Please refine');

      return false;
    }

    return true;
  }

  validWellSearch(): boolean {
    if ((this.wellSearch.address.length == 0) &&
      (this.wellSearch.city.length == 0) &&
      (this.wellSearch.zip.length == 0) &&
      (this.wellSearch.county.length == 0)) {
      this.toastr.error('Invalid Search.  Please refine');
      return false;
    }

    return true;
  }

  reset() {
    this.housingUnitSearchForm.reset();
    this.wellSearchForm.reset();
    this.wellForm.reset();
  }

  setHousingUnitSearchFieldsFromForm() {
    this.housingUnitSearch.contactName = this.housingUnitSearchForm.value.contactName;
    this.housingUnitSearch.address1 = this.housingUnitSearchForm.value.address1;
    this.housingUnitSearch.city = this.housingUnitSearchForm.value.city;
    this.housingUnitSearch.zip = this.housingUnitSearchForm.value.zip;
    this.housingUnitSearch.county = this.housingUnitSearchForm.value.county;
    this.housingUnitSearch.searchResultsMaxRows = '10';
  }

  setWellSearchFieldsFromForm() {
    this.wellSearch.address = this.wellSearchForm.value.address;
    this.wellSearch.city = this.wellSearchForm.value.city;
    this.wellSearch.zip = this.wellSearchForm.value.zip;
    this.wellSearch.county = this.wellSearchForm.value.county;
    this.wellSearch.searchResultsMaxRows = '10';
  }

  /** Announce the change in sort state for assistive technology. */
  announceSortChange(sortState: Sort) { }

  onChangeSelectedHousingUnit(housingUnitId: number) {
    this.selectedHousingUnitId = housingUnitId;
  }

  onChangeSelectedWell(wellId: number) {
    this.selectedWellId = wellId;
  }

  onChangeCreateNewWell() {
    if (this.housingUnitAssociations.createNewWell) {
      this.housingUnitAssociations.createNewWell = false;
    }
    else {
      this.housingUnitAssociations.createNewWell = true;
    }
  }

  associateHousingUnit() {
    this.loading = true;
    this.housingUnitAssociations.housingUnitId = this.selectedHousingUnitId > 0 ? this.selectedHousingUnitId : this.association.housingUnitId;
    this.housingUnitAssociations.wellId = this.association.wellId;

    this.housingUnitService.associateHousingUnitToWell(this.housingUnitAssociations).subscribe(
      result => {
        this.toastr.success("Housing Unit/Well Associated");
        this.loading = false;
        this.dialogRef.close();
      },
      error => {
        this.toastr.error("Unable to Associate Housing Unit/Well");
        this.selectedHousingUnitId = 0;
        this.loading = false;
      }
    );
  }

  associateWell() {
    this.loading = true;
    this.getWellFormValues();
    if (this.housingUnitAssociations.createNewWell == null) {
      this.housingUnitAssociations.createNewWell = false;
    }
    this.housingUnitService.associateHousingUnitToWell(this.housingUnitAssociations).subscribe(
      result => {
        this.toastr.success("Well/Housing Unit Associated");
        this.loading = false;
        this.dialogRef.close();
      },
      error => {
        this.toastr.error("Unable to Associate Well/Housing Unit");
        this.selectedHousingUnitId = 0;
        this.loading = false;
      }
    );
  }

  getWellFormValues() {
    this.housingUnitAssociations.housingUnitId = this.association.housingUnitId;
    this.housingUnitAssociations.wellId = this.selectedWellId > 0 ? this.selectedWellId : this.association.wellId;
    this.housingUnitAssociations.createNewWell = this.wellForm.value.createNewWell;

    if (this.housingUnitAssociations.createNewWell) {
      this.housingUnitAssociations.newAddress1 = this.wellSearchForm.get('address').value;
      this.housingUnitAssociations.newCity = this.wellSearchForm.get('city').value;
      this.housingUnitAssociations.newZip = this.wellSearchForm.get('zip').value;
      this.housingUnitAssociations.newCounty = this.wellSearchForm.get('county').value;
    }
  }
}